<template>
  <div class="vm-filter-card">
    <div class="vm-filter-card-header">
      <h4 class="vm-filter-card-title">Migration Filters</h4>
      <span v-if="loading" class="vm-filter-searching">Searching...</span>
    </div>

    <div class="vm-filter-grid">
      <div class="vm-filter-field">
        <label class="vm-filter-label">User</label>
        <div ref="userPickerRef" class="vm-search-select">
          <button
            type="button"
            class="form-control vm-search-select-trigger"
            :aria-expanded="userPickerOpen ? 'true' : 'false'"
            @click.stop="toggleUserPicker"
          >
            <span class="vm-search-select-trigger-label">{{ userTriggerLabel }}</span>
            <span>{{ userPickerOpen ? '▴' : '▾' }}</span>
          </button>

          <div v-if="userPickerOpen" class="vm-search-select-panel" @click.stop>
            <input
              v-model="userFilter"
              type="text"
              class="form-control vm-search-select-input"
              placeholder="Filter users"
            >
            <div class="vm-search-select-options">
              <button
                v-for="option in filteredUsers"
                :key="`user-${option.value || 'all'}`"
                type="button"
                class="vm-search-select-option"
                :class="{ 'is-selected': selectedUser === option.value }"
                @click="selectUser(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="vm-filter-field">
        <label class="vm-filter-label">VM State</label>
        <div ref="statePickerRef" class="vm-search-select">
          <button
            type="button"
            class="form-control vm-search-select-trigger"
            :aria-expanded="statePickerOpen ? 'true' : 'false'"
            @click.stop="toggleStatePicker"
          >
            <span class="vm-search-select-trigger-label">{{ stateTriggerLabel }}</span>
            <span>{{ statePickerOpen ? '▴' : '▾' }}</span>
          </button>

          <div v-if="statePickerOpen" class="vm-search-select-panel" @click.stop>
            <input
              v-model="stateFilter"
              type="text"
              class="form-control vm-search-select-input"
              placeholder="Filter VM states"
            >
            <div class="vm-search-select-options">
              <button
                v-for="option in filteredStates"
                :key="`state-${option.value || 'all'}`"
                type="button"
                class="vm-search-select-option"
                :class="{ 'is-selected': selectedState === option.value }"
                @click="selectState(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="vm-filter-field">
        <label class="vm-filter-label">HyperVisor</label>
        <div ref="hvPickerRef" class="vm-search-select">
          <button
            type="button"
            class="form-control vm-search-select-trigger"
            :aria-expanded="hvPickerOpen ? 'true' : 'false'"
            @click.stop="toggleHvPicker"
          >
            <span class="vm-search-select-trigger-label">{{ hvTriggerLabel }}</span>
            <span>{{ hvPickerOpen ? '▴' : '▾' }}</span>
          </button>

          <div v-if="hvPickerOpen" class="vm-search-select-panel" @click.stop>
            <input
              v-model="hvFilter"
              type="text"
              class="form-control vm-search-select-input"
              placeholder="Filter hypervisors"
            >
            <div class="vm-search-select-options">
              <button
                v-for="option in filteredHvs"
                :key="`hv-${option.value || 'all'}`"
                type="button"
                class="vm-search-select-option"
                :class="{ 'is-selected': selectedHv === option.value }"
                @click="selectHv(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="vm-filter-actions vm-filter-actions-inline">
        <button type="button" class="btn btn-default vm-filter-reset" @click="resetFilters">Reset Filters</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  endpoint: { type: String, required: true },
  pageUrl: { type: String, required: true },
  initialUser: { type: [String, Number], default: '' },
  initialState: { type: [String, Number], default: '' },
  initialHv: { type: [String, Number], default: '' },
  userOptions: { type: Array, default: () => [] },
  stateOptions: { type: Array, default: () => [] },
  hvOptions: { type: Array, default: () => [] }
})

const selectedUser = ref(String(props.initialUser || ''))
const selectedState = ref(String(props.initialState || ''))
const selectedHv = ref(String(props.initialHv || ''))
const loading = ref(false)
const userPickerOpen = ref(false)
const statePickerOpen = ref(false)
const hvPickerOpen = ref(false)
const userFilter = ref('')
const stateFilter = ref('')
const hvFilter = ref('')
const userPickerRef = ref(null)
const statePickerRef = ref(null)
const hvPickerRef = ref(null)
let requestTimer = null

const users = computed(() => normalizeOptions(props.userOptions))
const states = computed(() => normalizeOptions(props.stateOptions))
const hvs = computed(() => normalizeOptions(props.hvOptions))
const selectedUserDetail = computed(() => users.value.find((option) => option.value === selectedUser.value))
const selectedStateDetail = computed(() => states.value.find((option) => option.value === selectedState.value))
const selectedHvDetail = computed(() => hvs.value.find((option) => option.value === selectedHv.value))
const filteredUsers = computed(() => filterOptions(users.value, userFilter.value))
const filteredStates = computed(() => filterOptions(states.value, stateFilter.value))
const filteredHvs = computed(() => filterOptions(hvs.value, hvFilter.value))
const userTriggerLabel = computed(() => selectedUserDetail.value?.label || 'Select user')
const stateTriggerLabel = computed(() => selectedStateDetail.value?.label || 'Select VM state')
const hvTriggerLabel = computed(() => selectedHvDetail.value?.label || 'Select hypervisor')

watch([selectedUser, selectedState, selectedHv], () => {
  scheduleFetch()
})

onMounted(() => {
  document.addEventListener('click', closePickersOnOutsideClick)
  document.addEventListener('scc:hypervisor-migrate:refresh', fetchResults)
  fetchResults()
})

onBeforeUnmount(() => {
  window.clearTimeout(requestTimer)
  document.removeEventListener('click', closePickersOnOutsideClick)
  document.removeEventListener('scc:hypervisor-migrate:refresh', fetchResults)
})

function normalizeOptions(options) {
  return (options || []).map((option) => ({
    ...option,
    value: String(option.value || '')
  }))
}

function filterOptions(options, keywordValue) {
  const keyword = String(keywordValue || '').trim().toLowerCase()

  if (!keyword) {
    return options
  }

  return options.filter((option) => option.label.toLowerCase().includes(keyword))
}

function resetFilters() {
  selectedUser.value = ''
  selectedState.value = ''
  selectedHv.value = ''
  userFilter.value = ''
  stateFilter.value = ''
  hvFilter.value = ''
  userPickerOpen.value = false
  statePickerOpen.value = false
  hvPickerOpen.value = false
}

function scheduleFetch() {
  window.clearTimeout(requestTimer)
  requestTimer = window.setTimeout(fetchResults, 180)
}

function selectUser(value) {
  selectedUser.value = value
  userPickerOpen.value = false
  userFilter.value = ''
}

function selectState(value) {
  selectedState.value = value
  statePickerOpen.value = false
  stateFilter.value = ''
}

function selectHv(value) {
  selectedHv.value = value
  hvPickerOpen.value = false
  hvFilter.value = ''
}

function toggleUserPicker() {
  userPickerOpen.value = !userPickerOpen.value
  statePickerOpen.value = false
  hvPickerOpen.value = false
  if (!userPickerOpen.value) userFilter.value = ''
}

function toggleStatePicker() {
  statePickerOpen.value = !statePickerOpen.value
  userPickerOpen.value = false
  hvPickerOpen.value = false
  if (!statePickerOpen.value) stateFilter.value = ''
}

function toggleHvPicker() {
  hvPickerOpen.value = !hvPickerOpen.value
  userPickerOpen.value = false
  statePickerOpen.value = false
  if (!hvPickerOpen.value) hvFilter.value = ''
}

function closePickersOnOutsideClick(event) {
  if (userPickerRef.value && !userPickerRef.value.contains(event.target)) {
    userPickerOpen.value = false
    userFilter.value = ''
  }

  if (statePickerRef.value && !statePickerRef.value.contains(event.target)) {
    statePickerOpen.value = false
    stateFilter.value = ''
  }

  if (hvPickerRef.value && !hvPickerRef.value.contains(event.target)) {
    hvPickerOpen.value = false
    hvFilter.value = ''
  }
}

async function fetchResults() {
  loading.value = true
  document.dispatchEvent(new CustomEvent('scc:hypervisor-migrate-results:loading', {
    detail: { loading: true }
  }))

  const params = new URLSearchParams()
  if (selectedUser.value) params.append('search[user]', selectedUser.value)
  if (selectedState.value) params.append('search[state]', selectedState.value)
  if (selectedHv.value) params.append('search[hv]', selectedHv.value)

  const url = params.toString() ? `${props.endpoint}?${params.toString()}` : props.endpoint
  const pageUrl = params.toString() ? `${props.pageUrl}?${params.toString()}` : props.pageUrl

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) throw new Error('Failed to update migration results.')

    const data = await response.json()
    if (!data || !data.results_data) {
      throw new Error('Filtered migration table could not be loaded.')
    }

    document.dispatchEvent(new CustomEvent('scc:hypervisor-migrate-results:update', {
      detail: data.results_data
    }))

    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', pageUrl)
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
    document.dispatchEvent(new CustomEvent('scc:hypervisor-migrate-results:loading', {
      detail: { loading: false }
    }))
  }
}
</script>

<style scoped>
.vm-filter-card {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid rgba(135, 156, 181, 0.28);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(244, 247, 251, 0.95));
  box-shadow: 0 16px 40px rgba(134, 151, 172, 0.16);
}

.vm-filter-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.vm-filter-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2e4661;
}

.vm-filter-searching {
  font-size: 12px;
  font-weight: 600;
  color: #69839e;
}

.vm-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 16px;
  align-items: end;
}

.vm-filter-field {
  min-width: 0;
}

.vm-filter-label {
  display: inline-block;
  margin-bottom: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7b8da3;
}

.vm-filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.vm-filter-reset {
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(125, 144, 167, 0.32);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(239, 244, 249, 0.98));
  color: #3e556f;
  font-weight: 600;
  box-shadow: 0 10px 24px rgba(142, 157, 179, 0.12);
}

.vm-filter-reset:hover {
  background: linear-gradient(180deg, rgba(247, 250, 253, 1), rgba(232, 238, 245, 1));
  color: #24384e;
}

.vm-search-select {
  position: relative;
}

.vm-search-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  width: 100%;
  padding: 0 14px;
  border: 1px solid rgba(135, 156, 181, 0.32);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #33485f;
  text-align: left;
}

.vm-search-select-trigger-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vm-search-select-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  padding: 12px;
  border: 1px solid rgba(135, 156, 181, 0.25);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 45px rgba(138, 154, 177, 0.22);
}

.vm-search-select-input {
  margin-bottom: 10px;
}

.vm-search-select-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.vm-search-select-option {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: rgba(244, 247, 251, 0.92);
  color: #33485f;
  text-align: left;
}

.vm-search-select-option:hover,
.vm-search-select-option.is-selected {
  border-color: rgba(104, 142, 186, 0.28);
  background: rgba(227, 236, 247, 0.98);
}

@media (max-width: 991px) {
  .vm-filter-grid {
    grid-template-columns: 1fr;
  }

  .vm-filter-actions {
    justify-content: stretch;
  }

  .vm-filter-reset {
    width: 100%;
  }
}
</style>
